import React, {useEffect, useState} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFDatePicker from "@/components/Input/DatePicker";
import VenueSelect from "@/components/VenueSelect";
import VLFSelectNew from "@/components/Input/SelectNew";

import {RosterAPI} from "@/api/roster";
import {TrainingAPI} from "@/api/training";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	getInternalClubId,
	rpeOptions,
} from "@/utils/constants";
import {checkRequiredFields, visibleRosters} from "@/utils/functions";

const TRAINING_TIME_ERROR =
	"Inserire date valide: la fine deve essere successiva all'inizio.";

const HandleTrainingPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const location = useLocation();

	const userId = useSelector((state) => state?.user?.userId);
	const clubId = useSelector((state) => state?.user?.clubId);
	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const userName = useSelector((state) => state?.user?.userName);
	const seasonId = useSelector((state) => state?.season?.id);
	const season = useSelector((state) => state?.season);

	const [rosters, setRosters] = useState([]);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const prefilledDate = location.state?.startDate
		? new Date(location.state.startDate)
		: null;

	const [fields, setFields] = useState({
		roster: null,
		fieldName: null,
		startDate: prefilledDate,
		endDate: prefilledDate,
	});

	// ── Rosters ───────────────────────────────────────────────────────────────

	const getRosters = async () => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				prospect: false,
				clubId: getInternalClubId(),
			});
			setRosters(
				visibleRosters(
					(response || []).filter(
						(r) => !r.clubId || r.clubId === getInternalClubId(),
					),
				).map((r) => ({
					value: r.id,
					label: r.name,
				})),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		getRosters();
	}, []);

	// ── Edit load ─────────────────────────────────────────────────────────────

	const getTraining = async () => {
		setLoading(true);
		try {
			const response = await TrainingAPI.getById(id);
			response.startDate = response.startDate
				? new Date(response.startDate)
				: null;
			response.endDate = response.endDate ? new Date(response.endDate) : null;

			if (response.rosterId) {
				response.roster = {
					value: response.rosterId,
					label: response.roster?.name ?? response.rosterId,
				};
			} else if (response.playersCategoryId) {
				// legacy fallback
				response.roster = {
					value: response.playersCategoryId,
					label: response.playersCategory ?? response.playersCategoryId,
				};
			}

			response.fieldName = {
				value: response.venueId,
				label: response.fieldName,
			};
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) getTraining();
	}, [edit]);

	// ── Validation ─────────────────────────────────────────────────────────────

	const getRequiredFields = () => ["startDate", "endDate", "roster"];

	const getDateErrors = () => {
		const {startDate: start, endDate: end} = fields;
		const invalid = [];
		if (start && !moment(start).isValid()) invalid.push("startDate");
		if (
			end &&
			(!moment(end).isValid() ||
				(start && moment(start).isValid() && !moment(end).isAfter(start)))
		)
			invalid.push("endDate");
		return invalid;
	};

	// ── Save ───────────────────────────────────────────────────────────────────

	const createRPE = async (trainingId) => {
		try {
			await TrainingAPI.createMeasurement({
				trainingId,
				playerId,
				rpe: fields?.rpe?.value,
			});
		} catch (error) {
			setLoading(false);
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const saveTraining = async () => {
		try {
			const dateErrors = getDateErrors();
			const errs = [
				...new Set([
					...checkRequiredFields(getRequiredFields(), fields),
					...dateErrors,
				]),
			];
			setErrors(errs);
			if (dateErrors.length) handleNotification(TRAINING_TIME_ERROR, "error");

			if (!errs.length) {
				setLoading(true);

				const body = {
					...fields,
					rosterId: fields?.roster?.value,
					trainingConvocations: [],
					clubId,
					userId,
					user: {userId, userName},
					venueId: fields?.fieldName?.value || null,
					fieldName: fields?.fieldName?.label || null,
				};

				const response = edit
					? await TrainingAPI.update(id, body)
					: await TrainingAPI.create(body);

				if (userRole === "Player" && fields?.rpe) {
					await createRPE(response.id);
				}

				setTimeout(() => {
					navigate(`/trainings/${response?.id || id}`);
					setLoading(false);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			}
		} catch (error) {
			setLoading(false);
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveTraining();
	};

	const goBack = () => {
		if (edit) navigate("/trainings/" + id);
		else navigate("/trainings");
	};

	// ── Actions ────────────────────────────────────────────────────────────────

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: !!checkRequiredFields(getRequiredFields(), fields).length,
		},
	];

	const breadcrumbsElements = [
		userRole === "Player"
			? {label: "Allenamenti", path: "/trainings"}
			: {label: "Calendario", path: "/calendar"},
	];

	// ── Render ─────────────────────────────────────────────────────────────────

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuovo allenamento"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											type="datetime"
											label="Inizio"
											value={fields.startDate}
											error={errors.includes("startDate")}
											helperText={REQUIRED_FIELD}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, startDate: value});
											}}
											required
											size="small"
											minDate={
												season?.startDate
													? new Date(season.startDate)
													: undefined
											}
											maxDate={
												season?.endDate ? new Date(season.endDate) : undefined
											}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											type="datetime"
											label="Fine"
											value={fields.endDate}
											error={errors.includes("endDate")}
											helperText={
												fields.endDate ? TRAINING_TIME_ERROR : REQUIRED_FIELD
											}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, endDate: value});
											}}
											required
											size="small"
											minDate={
												season?.startDate
													? new Date(season.startDate)
													: undefined
											}
											maxDate={
												season?.endDate ? new Date(season.endDate) : undefined
											}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={rosters}
											value={fields.roster}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, roster: newValue});
											}}
											label="Rosa"
											required
											helperText={
												errors.indexOf("roster") !== -1 ? REQUIRED_FIELD : ""
											}
											error={errors.indexOf("roster") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VenueSelect
											training
											handleNotification={handleNotification}
											value={fields.fieldName}
											onChange={(newValue) => {
												setFields((prev) => ({...prev, fieldName: newValue}));
											}}
											label="Campo"
											required
										/>
									</div>
									{userRole === "Player" && (
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={rpeOptions}
												value={fields.rpe}
												onChange={(value) => {
													setFields({...fields, rpe: value});
												}}
												label="RPE"
												sorted={false}
											/>
										</div>
									)}
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleTrainingPage;
